import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUp02Icon,
  Cancel01Icon,
  CustomerSupportIcon,
  FileAttachmentIcon,
  Image01Icon,
  PlusSignIcon,
} from '@hugeicons/core-free-icons';
import { Button } from '../../components/ui/Button';
import { CustomActionMenu } from '../../components/ui/CustomActionMenu';
import { CustomSelect } from '../../components/ui/CustomSelect';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { Input } from '../../components/ui/Input';
import { StatusPill } from '../../components/ui/StatusPill';
import { Card } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from '../../components/ui/attachment';
import { Bubble, BubbleContent } from '../../components/ui/bubble';
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
} from '../../components/ui/message';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from '../../components/ui/message-scroller';
import {
  createTicket,
  deleteTicketAttachment,
  replyToTicket,
  updateTicketStatus,
  uploadTicketAttachment,
  type ConsoleState,
  type Ticket,
  type TicketAttachment,
} from '../../lib/api';
import {
  TicketDirectoryCard,
  TicketDirectoryToolbar,
  type TicketPriorityFilter,
  type TicketStatusFilter,
} from './TicketDirectory';

const priorityOptions: Array<{ label: string; value: 'low' | 'normal' | 'high' }> = [
  { label: 'Low priority', value: 'low' },
  { label: 'Normal priority', value: 'normal' },
  { label: 'High priority', value: 'high' },
];

const ticketStatusOptions = [
  { label: 'Open', value: 'open' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Resolved', value: 'closed' },
] as const;

type TicketStatus = (typeof ticketStatusOptions)[number]['value'];

const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const imageAccept = 'image/avif,image/gif,image/jpeg,image/png,image/webp';
const documentAccept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf';
const attachmentMenuOptions = [
  { icon: <HugeIcon icon={Image01Icon} size={18} />, label: 'Add images', value: 'image' },
  { icon: <HugeIcon icon={FileAttachmentIcon} size={18} />, label: 'Add documents', value: 'document' },
] as const;

type PendingAttachment = {
  id: string;
  file: File;
  previewUrl: string | null;
};

export function TicketsPage({
  onStateChange,
  tickets,
}: {
  tickets: Ticket[];
  onStateChange: React.Dispatch<React.SetStateAction<ConsoleState | null>>;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<TicketPriorityFilter>('all');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TicketStatusFilter>('all');
  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedId) ?? null,
    [selectedId, tickets]
  );
  const visibleTickets = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return tickets.filter((ticket) =>
      (statusFilter === 'all' || ticket.status === statusFilter)
      && (priorityFilter === 'all' || ticket.priority === priorityFilter)
      && (!normalizedQuery || ticket.subject.toLowerCase().includes(normalizedQuery))
    );
  }, [priorityFilter, query, statusFilter, tickets]);

  function replaceTicket(ticket: Ticket) {
    onStateChange((current) => {
      if (!current) return current;
      const exists = current.tickets.some((item) => item.id === ticket.id);
      return {
        ...current,
        tickets: exists
          ? current.tickets.map((item) => (item.id === ticket.id ? ticket : item))
          : [ticket, ...current.tickets],
      };
    });
    setSelectedId(ticket.id);
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOpen(true)} size="sm" type="button">Open ticket</Button>
      </div>
      <div className={`${selectedTicket ? 'lg:h-[calc(100vh-9rem)] lg:min-h-[520px] lg:grid-cols-[320px_minmax(0,1fr)]' : ''} grid min-h-0 gap-3`}>
        <TicketDirectoryCard
          className={selectedTicket ? 'min-h-0 bg-card' : 'min-h-0'}
          compact={Boolean(selectedTicket)}
          information={visibleTickets.length ? `${visibleTickets.length} ${visibleTickets.length === 1 ? 'conversation' : 'conversations'} in this scope.` : 'No conversations in this scope.'}
          informationLabel="Support tickets information"
          toolbar={(
            <TicketDirectoryToolbar
              onPriorityChange={setPriorityFilter}
              onQueryChange={setQuery}
              onStatusChange={setStatusFilter}
              priority={priorityFilter}
              query={query}
              searchPlaceholder="Search ticket subjects"
              status={statusFilter}
            />
          )}
        >
          {visibleTickets.length ? (
            <div className={selectedTicket ? 'flex-1 bg-card' : undefined}>
              <Table className={selectedTicket ? 'table-fixed' : 'min-w-[760px]'}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className={selectedTicket ? 'w-[72%] pl-4' : 'pl-4'}>Subject</TableHead>
                    {selectedTicket ? null : <TableHead>Created</TableHead>}
                    {selectedTicket ? null : <TableHead>Priority</TableHead>}
                    <TableHead>Status</TableHead>
                    {selectedTicket ? null : <TableHead className="pr-4 text-right">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleTickets.map((ticket) => {
                    const isSelected = selectedTicket?.id === ticket.id;
                    return (
                      <TableRow
                        aria-current={isSelected ? 'true' : undefined}
                        className="cursor-pointer focus-visible:outline-none focus-visible:[&>td]:bg-accent/60 focus-visible:[&>td:first-child]:rounded-l-lg focus-visible:[&>td:last-child]:rounded-r-lg"
                        key={ticket.id}
                        onClick={() => setSelectedId(ticket.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedId(ticket.id);
                          }
                        }}
                        tabIndex={0}
                      >
                        <TableCell className="pl-4">
                          <p className="max-w-full truncate font-semibold" title={ticket.subject}>{ticket.subject}</p>
                          {selectedTicket ? <p className="text-xs text-muted-foreground">{formatDate(ticket.createdAt)}</p> : null}
                        </TableCell>
                        {selectedTicket ? null : <TableCell className="text-muted-foreground">{formatDate(ticket.createdAt)}</TableCell>}
                        {selectedTicket ? null : <TableCell>{formatPriority(ticket.priority)}</TableCell>}
                        <TableCell><StatusPill status={ticket.status} /></TableCell>
                        {selectedTicket ? null : <TableCell className="pr-4 text-right"><Button onClick={(event) => { event.stopPropagation(); setSelectedId(ticket.id); }} size="sm" type="button" variant="ghost">View</Button></TableCell>}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 p-5 text-center">
              <HugeIcon className="text-slate-500" icon={CustomerSupportIcon} size={28} strokeWidth={1.6} />
              <p className="text-sm font-semibold leading-5">{tickets.length ? 'No matching tickets' : 'No support tickets'}</p>
              <p className="max-w-[420px] text-xs leading-5 text-slate-500">
                {tickets.length
                  ? 'Adjust the search or filters to see more conversations.'
                  : 'Open a ticket when the organization needs help. Do not include customer document content or secrets.'}
              </p>
            </div>
          )}
        </TicketDirectoryCard>

        {selectedTicket ? (
          <TicketDetailPane onClose={() => setSelectedId(null)} onTicketChange={replaceTicket} ticket={selectedTicket} />
        ) : null}
      </div>

      {dialogOpen ? (
        <OpenTicketDialog
          onClose={() => setDialogOpen(false)}
          onCreated={(ticket) => {
            replaceTicket(ticket);
            setDialogOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

export function TicketDetailPane({
  contextLabel,
  deleteAttachment = deleteTicketAttachment,
  mode = 'customer',
  onClose,
  replyToActiveTicket = replyToTicket,
  onTicketChange,
  ticket,
  updateActiveTicketStatus = updateTicketStatus,
  uploadAttachment = uploadTicketAttachment,
}: {
  contextLabel?: string;
  deleteAttachment?: (ticketId: string, attachmentId: string) => Promise<void>;
  mode?: 'customer' | 'platform';
  onClose: () => void;
  replyToActiveTicket?: (ticketId: string, message: string, attachments: TicketAttachment[]) => Promise<Ticket>;
  onTicketChange: (ticket: Ticket) => void;
  ticket: Ticket | null;
  updateActiveTicketStatus?: (ticketId: string, status: TicketStatus) => Promise<Ticket>;
  uploadAttachment?: (ticketId: string, file: File) => Promise<TicketAttachment>;
}) {
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!message && textareaRef.current) textareaRef.current.style.height = '40px';
  }, [message]);

  useEffect(() => {
    setMessage('');
    setAttachmentError(null);
    setPendingAttachments([]);
  }, [ticket?.id]);

  useEffect(() => {
    pendingAttachmentsRef.current = pendingAttachments;
  }, [pendingAttachments]);

  useEffect(() => {
    return () => {
      pendingAttachmentsRef.current.forEach((attachment) => {
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      });
    };
  }, []);

  if (!ticket) return null;
  const activeTicket = ticket;

  async function submitReply(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!message.trim() && pendingAttachments.length === 0) return;
    setSaving(true);
    setAttachmentError(null);
    const uploaded: TicketAttachment[] = [];
    try {
      for (const attachment of pendingAttachments) {
        uploaded.push(await uploadAttachment(activeTicket.id, attachment.file));
      }
      const updated = await replyToActiveTicket(activeTicket.id, message, uploaded);
      onTicketChange(updated);
      setMessage('');
      pendingAttachments.forEach((attachment) => {
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      });
      setPendingAttachments([]);
      if (textareaRef.current) textareaRef.current.style.height = '40px';
    } catch (error) {
      await Promise.allSettled(
        uploaded.map((attachment) => deleteAttachment(activeTicket.id, attachment.id))
      );
      setAttachmentError(error instanceof Error ? error.message : 'Unable to send attachments.');
    } finally {
      setSaving(false);
    }
  }

  function addFiles(files: FileList | null) {
    if (!files?.length) return;
    setAttachmentError(null);
    const available = MAX_ATTACHMENTS - pendingAttachments.length;
    if (available <= 0) {
      setAttachmentError(`Up to ${MAX_ATTACHMENTS} attachments can be sent at once.`);
      return;
    }

    const selected = Array.from(files).slice(0, available);
    const valid: PendingAttachment[] = [];
    for (const file of selected) {
      if (file.size <= 0 || file.size > MAX_ATTACHMENT_BYTES) {
        setAttachmentError(`${file.name} must be 10 MB or smaller.`);
        continue;
      }
      valid.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
    }
    if (files.length > available) {
      setAttachmentError(`Up to ${MAX_ATTACHMENTS} attachments can be sent at once.`);
    }
    setPendingAttachments((current) => [...current, ...valid]);
  }

  function removePendingAttachment(id: string) {
    setPendingAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return current.filter((attachment) => attachment.id !== id);
    });
  }

  async function changeTicketStatus(status: TicketStatus) {
    setSaving(true);
    try {
      onTicketChange(await updateActiveTicketStatus(activeTicket.id, status));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="flex h-full min-h-0 flex-col gap-0 overflow-hidden py-0" size="sm">
      <div className="border-b p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold">{activeTicket.subject}</h3>
            <p className="mt-1 text-xs font-medium leading-5 text-slate-500">{activeTicket.id}</p>
            {contextLabel ? <p className="mt-1 text-xs font-semibold leading-5 text-slate-600">{contextLabel}</p> : null}
          </div>
          <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
            {mode === 'platform' ? (
              <div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
                <CustomSelect
                  ariaLabel="Ticket status"
                  className="bg-slate-100 hover:bg-slate-200/80"
                  fullWidth
                  onChange={(status) => {
                    void changeTicketStatus(status);
                  }}
                  options={ticketStatusOptions}
                  value={activeTicket.status}
                  variant="pill"
                />
              </div>
            ) : null}
            <Button
              className="text-xs"
              size="sm"
              disabled={saving}
              onClick={() => {
                void changeTicketStatus(activeTicket.status === 'closed' ? 'open' : 'closed');
            }}
            type="button"
            variant="default"
            >
              {activeTicket.status === 'closed' ? 'Reopen ticket' : 'Mark resolved'}
            </Button>
            <Button
              aria-label="Close ticket details"
              size="icon-sm"
              onClick={onClose}
              type="button"
              variant="destructive"
            >
              <HugeIcon icon={Cancel01Icon} size={15} />
            </Button>
          </div>
        </div>
      </div>

      <MessageScrollerProvider>
        <MessageScroller aria-label="Ticket conversation" className="console-message-scroller flex-1" role="log">
          <MessageScrollerViewport>
            <MessageScrollerContent className="justify-end gap-5 p-4">
              {activeTicket.messages.length ? activeTicket.messages.map((item) => {
              const isCustomer = item.authorType === 'customer';
              const initials = item.authorName
                .split(/\s+/)
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              return (
                <MessageScrollerItem key={item.id} messageId={item.id} scrollAnchor={isCustomer}>
                  <Message align={isCustomer ? 'end' : 'start'}>
                    <MessageAvatar className="size-8 text-xs font-semibold text-muted-foreground">
                      {initials}
                    </MessageAvatar>
                    <MessageContent className="max-w-[min(82%,560px)]">
                    {item.attachments.length ? (
                      <MessageAttachments attachments={item.attachments} isCustomer={isCustomer} />
                    ) : null}
                    {item.body ? (
                      <Bubble align={isCustomer ? 'end' : 'start'} variant={isCustomer ? 'muted' : 'outline'}>
                        <BubbleContent><p className="whitespace-pre-wrap">{item.body}</p></BubbleContent>
                      </Bubble>
                    ) : null}
                    <MessageFooter>{item.authorName} · {formatDate(item.createdAt)}</MessageFooter>
                    </MessageContent>
                  </Message>
                </MessageScrollerItem>
              );
              }) : (
                <div className="flex min-h-40 items-center justify-center rounded-xl border border-dashed bg-muted/30 px-5 text-center">
                  <p className="text-sm text-muted-foreground">No discussion yet. Send the first reply below.</p>
                </div>
              )}
            </MessageScrollerContent>
          </MessageScrollerViewport>
          <MessageScrollerButton />
        </MessageScroller>
      </MessageScrollerProvider>

      <form
        className="border-t bg-card p-3"
        onSubmit={(event) => {
          void submitReply(event);
        }}
      >
        <input accept={imageAccept} className="hidden" multiple onChange={(event) => { addFiles(event.target.files); event.currentTarget.value = ''; }} ref={imageInputRef} type="file" />
        <input accept={documentAccept} className="hidden" multiple onChange={(event) => { addFiles(event.target.files); event.currentTarget.value = ''; }} ref={documentInputRef} type="file" />
        <div className="relative rounded-3xl bg-muted p-3">
          {pendingAttachments.length ? (
            <PendingAttachmentPreviews attachments={pendingAttachments} onRemove={removePendingAttachment} />
          ) : null}
          <Textarea
            className="h-10 min-h-10 max-h-40 w-full resize-none border-0 bg-transparent px-2 py-2 text-base shadow-none focus-visible:ring-0"
            disabled={activeTicket.status === 'closed'}
            onChange={(event) => {
              const nextMessage = event.target.value;
              setMessage(nextMessage);
              const nextLineCount = nextMessage.split('\n').length;
              event.currentTarget.style.height = `${Math.min(160, Math.max(40, nextLineCount * 24 + 16))}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder={activeTicket.status === 'closed' ? 'Ticket is resolved.' : 'Write a reply…'}
            ref={textareaRef}
            rows={1}
            value={message}
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <CustomActionMenu
              ariaLabel="Add attachment"
              contentClassName="min-w-44 [&_[role=menuitem]]:whitespace-nowrap"
              disabled={saving || activeTicket.status === 'closed' || pendingAttachments.length >= MAX_ATTACHMENTS}
              onSelect={(value) => {
                if (value === 'image') imageInputRef.current?.click();
                else documentInputRef.current?.click();
              }}
              options={attachmentMenuOptions}
              trigger={({ menuId, open }) => (
                <Button
                  aria-controls={menuId}
                  aria-expanded={open}
                  aria-haspopup="menu"
                  aria-label="Add attachment"
                  className="size-10 min-h-0 shrink-0 rounded-full border bg-background p-0 shadow-none hover:bg-background"
                  disabled={saving || activeTicket.status === 'closed' || pendingAttachments.length >= MAX_ATTACHMENTS}
                  type="button"
                  variant="ghost"
                >
                  <HugeIcon icon={PlusSignIcon} size={20} strokeWidth={1.8} />
                </Button>
              )}
            />
            <Button aria-label="Send reply" className="size-10 min-h-0 shrink-0 rounded-full p-0 disabled:opacity-50" disabled={saving || activeTicket.status === 'closed' || (!message.trim() && pendingAttachments.length === 0)} type="submit">
              <HugeIcon icon={ArrowUp02Icon} size={20} strokeWidth={2.2} />
            </Button>
          </div>
        </div>
        {attachmentError ? <p className="mt-2 px-2 text-center text-xs font-medium text-rose-600">{attachmentError}</p> : null}
        <p className="mt-1 px-2 text-center text-xs text-muted-foreground">Replies are visible to the customer.</p>
      </form>
    </Card>
  );
}

function PendingAttachmentPreviews({
  attachments,
  onRemove,
}: {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
}) {
  return (
    <AttachmentGroup className="px-1 pb-2">
      {attachments.map((attachment) => (
        <Attachment key={attachment.id} size="sm" state="idle">
          <AttachmentMedia variant={attachment.previewUrl ? 'image' : 'icon'}>
            {attachment.previewUrl ? <img alt={attachment.file.name} src={attachment.previewUrl} /> : <HugeIcon icon={FileAttachmentIcon} size={18} />}
          </AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle>{attachment.file.name}</AttachmentTitle>
            <AttachmentDescription>{formatFileSize(attachment.file.size)}</AttachmentDescription>
          </AttachmentContent>
          <AttachmentActions>
            <AttachmentAction aria-label={`Remove ${attachment.file.name}`} onClick={() => onRemove(attachment.id)} type="button">
              <HugeIcon icon={Cancel01Icon} size={13} />
            </AttachmentAction>
          </AttachmentActions>
        </Attachment>
      ))}
    </AttachmentGroup>
  );
}

function MessageAttachments({
  attachments,
  isCustomer,
}: {
  attachments: TicketAttachment[];
  isCustomer: boolean;
}) {
  return (
    <AttachmentGroup className={isCustomer ? 'justify-end' : 'justify-start'}>
      {attachments.map((attachment) =>
        <Attachment key={attachment.id} orientation={attachment.kind === 'image' ? 'vertical' : 'horizontal'}>
          <AttachmentTrigger render={<a href={attachment.url} rel="noreferrer" target="_blank" />} />
          <AttachmentMedia variant={attachment.kind === 'image' ? 'image' : 'icon'}>
            {attachment.kind === 'image' ? <img alt={attachment.fileName} loading="lazy" src={attachment.url} /> : <HugeIcon icon={FileAttachmentIcon} size={18} />}
          </AttachmentMedia>
          <AttachmentContent>
            <AttachmentTitle>{attachment.fileName}</AttachmentTitle>
            <AttachmentDescription>{formatFileSize(attachment.sizeBytes)}</AttachmentDescription>
          </AttachmentContent>
        </Attachment>
      )}
    </AttachmentGroup>
  );
}

function OpenTicketDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (ticket: Ticket) => void;
}) {
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      onCreated(await createTicket({ subject, priority, message }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create ticket.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog onOpenChange={(open) => { if (!open) onClose(); }} open>
      <DialogContent className="sm:max-w-lg">
      <form
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <DialogHeader>
          <DialogTitle>Open ticket</DialogTitle>
          <DialogDescription>Describe the issue for the support team. Do not include secrets.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-4">
          <Input
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Subject"
            required
            value={subject}
          />
          <CustomSelect
            ariaLabel="Ticket priority"
            onChange={setPriority}
            options={priorityOptions}
            value={priority}
          />
          <Textarea
            className="min-h-28 resize-none"
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Describe the issue. Do not include documents or secrets."
            required
            value={message}
          />
          {error ? <p className="text-xs font-medium leading-5 text-rose-700">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button disabled={saving} onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={saving} type="submit">
            {saving ? 'Opening' : 'Open ticket'}
          </Button>
        </DialogFooter>
      </form>
      </DialogContent>
    </Dialog>
  );
}

function formatPriority(priority: string) {
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1)}`;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
